import { addMinutes, format as dateFNSFormatDate, isBefore } from 'date-fns'

export type Timestamp = {
  seconds?: number
}

export const DATE_FORMAT = 'd MMM yyyy'

export const DATETIME_YEARLESS_FORMAT = 'd MMMM, HH:mm'

export const ONE_HOUR_IN_MINUTES = 60

export const USER_DATETIMES = () => [
  {
    dateFormat: 'd MMM yyyy',
    datetimeFormat: 'd MMM yyyy, HH:mm',
    label: `d MMM yyyy (e.g. ${dateFNSFormatDate(new Date(), 'd MMM yyyy')})`
  },
  {
    dateFormat: 'MMM d, yyyy',
    datetimeFormat: 'MMM d, yyyy, HH:mm',
    label: `MMM d, yyyy (e.g. ${dateFNSFormatDate(new Date(), 'MMM d, yyyy')})`
  },
  {
    dateFormat: 'd.M.yyyy',
    datetimeFormat: 'd.M.yyyy, HH:mm',
    label: `d.M.yyyy (e.g. ${dateFNSFormatDate(new Date(), 'd.M.yyyy')})`
  },
  {
    dateFormat: 'M/d/yyyy',
    datetimeFormat: 'M/d/yyyy, HH:mm',
    label: `M/d/yyyy (e.g. ${dateFNSFormatDate(new Date(), 'M/d/yyyy')})`
  }
]

const dateToUTCSeconds = (date: Date) =>
  Math.round(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds()
    ) / 1000
  )

const dateToSeconds = (date: Date) => Math.floor(date.getTime() / 1000)

export const getTimeStamp: (
  date: Date,
  utc?: boolean
) => {
  seconds: number
} = (date, utc) => ({
  seconds: utc ? dateToUTCSeconds(date) : dateToSeconds(date)
})

const getDateFromUTCTimeStamp = (timeStamp: Timestamp) => {
  const d = new Date(timeStamp?.seconds * 1000)
  const utcDate = new Date(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
    d.getUTCHours(),
    d.getUTCMinutes(),
    d.getUTCSeconds()
  )

  return utcDate
}

export const isValidDate = (timeStamp: Timestamp | undefined) =>
  timeStamp?.seconds > 0

export const getDateFromTimeStamp = (timeStamp: Timestamp, utc?: boolean) => {
  if (utc) {
    return getDateFromUTCTimeStamp(timeStamp)
  }

  return new Date(timeStamp.seconds * 1000)
}

export const getDateFromTimeStampOrNull = (
  timeStamp: Timestamp | null | undefined,
  utc?: boolean
): Date | null => {
  if (!isValidDate(timeStamp)) return null

  return getDateFromTimeStamp(timeStamp, utc)
}

export const enforceFutureTimeStamp = (timeStamp: Timestamp) => {
  if (!isValidDate(timeStamp)) return null

  const now = new Date()
  if (isBefore(getDateFromTimeStamp(timeStamp), now)) {
    return getTimeStamp(addMinutes(now, 1))
  }

  return timeStamp
}

export const formatDate = ({
  timeStamp,
  withTime = false,
  utc = false,
  dateFormatPreferences
}: {
  timeStamp: Timestamp | undefined
  withTime?: boolean
  utc?: boolean
  dateFormatPreferences?: string
}) => {
  if (!isValidDate(timeStamp)) return null

  const DATETIMES = USER_DATETIMES()

  const fullDateFormat =
    DATETIMES.find((d) => d.dateFormat === dateFormatPreferences) ??
    DATETIMES[0]

  const formatString = withTime
    ? fullDateFormat.datetimeFormat
    : fullDateFormat.dateFormat

  return dateFNSFormatDate(
    getDateFromTimeStamp(timeStamp, utc ?? !withTime),
    formatString
  )
}
